/**
 * The route table and the role guards. OWNER: web-frontend-engineer.
 *
 * `[T-33]`: every route below carries an opaque id or an order reference and
 * NOTHING ELSE. There is no measurement, body descriptor or specification value
 * in any path, query string or fragment in this table, and none in a document
 * title (`AppShell` sets it from the page name alone).
 *
 * A client reaching `/atelier/*` renders `ST-403`, which is byte-identical to
 * `ST-404` — distinguishing them leaks whether an order or a client exists.
 */

import { Component, type ReactNode } from 'react';
import { t } from '@pt/copy';
import { AppShell } from './AppShell.tsx';
import { AnnouncerProvider } from './Announcer.tsx';
import { NavigationProvider, matchPath, useNavigation } from './Router.tsx';
import { SessionProvider, useSession } from './SessionProvider.tsx';
import { Refused } from '../components/states.tsx';
import { Button, Link, Statement } from '../components/primitives.tsx';
import { ResetScreen, SignInScreen } from '../screens/SignIn.tsx';
import { HomeScreen } from '../screens/Home.tsx';
import { MeasurementsScreen, PrivacyScreen, RemeasureScreen } from '../screens/Measurements.tsx';
import { BookScreen, ClothScreen, SwatchScreen } from '../screens/Book.tsx';
import { ClothStepScreen, CommissionScreen, ConfigScreen } from '../screens/Commission.tsx';
import { PlacedScreen, ReviewScreen } from '../screens/Review.tsx';
import { OrderAskScreen, OrderScreen } from '../screens/Order.tsx';
import { QueueScreen } from '../screens/atelier/Queue.tsx';
import { AtelierOrderScreen } from '../screens/atelier/OrderDetail.tsx';
import { CutTicketsScreen } from '../screens/atelier/CutTickets.tsx';
import {
  ClientScreen,
  ClientsScreen,
  MeasurementAuthoringScreen,
} from '../screens/atelier/Clients.tsx';
import { LeadScreen, LeadsScreen } from '../screens/atelier/Leads.tsx';
import { ClientNewScreen } from '../screens/atelier/ClientNew.tsx';
import { PhotographSetScreen } from '../screens/atelier/Photographs.tsx';
import { ClothDetailScreen, ClothListScreen, SwatchQueueScreen } from '../screens/atelier/Cloth.tsx';
import { GlossaryScreen } from '../screens/atelier/Glossary.tsx';
import { RetentionScreen } from '../screens/atelier/Retention.tsx';

type Area = 'public' | 'client' | 'staff' | 'backoffice';

interface Route {
  readonly path: string;
  readonly area: Area;
  readonly render: (params: Readonly<Record<string, string>>, query: URLSearchParams) => ReactNode;
}

const ROUTES: readonly Route[] = [
  { path: '/signin', area: 'public', render: () => <SignInScreen /> },
  { path: '/reset', area: 'public', render: () => <ResetScreen /> },

  { path: '/wardrobe', area: 'client', render: () => <HomeScreen /> },
  { path: '/measurements', area: 'client', render: () => <MeasurementsScreen /> },
  { path: '/measurements/remeasure', area: 'client', render: () => <RemeasureScreen /> },
  {
    path: '/measurements/:recordId',
    area: 'client',
    render: (params) => <MeasurementsScreen recordId={params['recordId'] as string} />,
  },
  { path: '/privacy', area: 'client', render: () => <PrivacyScreen /> },
  { path: '/book', area: 'client', render: () => <BookScreen /> },
  {
    path: '/book/:clothId',
    area: 'client',
    render: (params) => <ClothScreen clothId={params['clothId'] as string} />,
  },
  {
    path: '/book/:clothId/swatch',
    area: 'client',
    render: (params) => <SwatchScreen clothId={params['clothId'] as string} />,
  },
  {
    path: '/commission',
    area: 'client',
    render: (_params, query) => {
      const cloth = query.get('cloth');
      return <CommissionScreen {...(cloth === null ? {} : { seedClothId: cloth })} />;
    },
  },
  { path: '/commission/cloth', area: 'client', render: () => <ClothStepScreen /> },
  {
    path: '/commission/cloth/:clothId',
    area: 'client',
    render: (params) => <ClothScreen clothId={params['clothId'] as string} />,
  },
  { path: '/commission/make', area: 'client', render: () => <ConfigScreen /> },
  { path: '/commission/review', area: 'client', render: () => <ReviewScreen /> },
  {
    path: '/placed/:orderRef',
    area: 'client',
    render: (params) => <PlacedScreen orderRef={params['orderRef'] as string} />,
  },
  {
    path: '/orders/:orderRef',
    area: 'client',
    render: (params) => <OrderScreen orderRef={params['orderRef'] as string} />,
  },
  {
    path: '/orders/:orderRef/ask',
    area: 'client',
    render: (params, query) => (
      <OrderAskScreen
        orderRef={params['orderRef'] as string}
        kind={query.get('kind') === 'cancel' ? 'cancel' : 'change'}
      />
    ),
  },

  { path: '/atelier/orders', area: 'staff', render: () => <QueueScreen /> },
  { path: '/atelier/production', area: 'staff', render: () => <QueueScreen production /> },
  {
    path: '/atelier/orders/:orderRef',
    area: 'staff',
    render: (params) => <AtelierOrderScreen orderRef={params['orderRef'] as string} />,
  },
  /* LAW 59: one ticket a garment, from the same frozen sheet. Staff, like the
     order: the workshop reads the tickets, the back office draws on them. */
  {
    path: '/atelier/orders/:orderRef/tickets',
    area: 'staff',
    render: (params) => <CutTicketsScreen orderRef={params['orderRef'] as string} />,
  },
  { path: '/atelier/cloth', area: 'staff', render: () => <ClothListScreen /> },
  {
    path: '/atelier/cloth/:clothId',
    area: 'staff',
    render: (params) => <ClothDetailScreen clothId={params['clothId'] as string} />,
  },
  { path: '/atelier/swatches', area: 'backoffice', render: () => <SwatchQueueScreen /> },
  /* P-THAI-01's desk. BACKOFFICE: a signature carries a person's name onto every
     work order the term prints on, so it is not the workshop's to give. */
  { path: '/atelier/glossary', area: 'backoffice', render: () => <GlossaryScreen /> },
  /* P-RETAIN-01's report. BACKOFFICE, and it deletes nothing. */
  { path: '/atelier/retention', area: 'backoffice', render: () => <RetentionScreen /> },
  /* WIRE-1: the site's commissions. BACKOFFICE — a name and a number. */
  { path: '/atelier/leads', area: 'backoffice', render: () => <LeadsScreen /> },
  {
    path: '/atelier/leads/:leadId',
    area: 'backoffice',
    render: (params) => <LeadScreen leadId={params['leadId'] as string} />,
  },
  { path: '/atelier/clients', area: 'backoffice', render: () => <ClientsScreen /> },
  /* BEFORE `/atelier/clients/:clientId` — `matchPath` takes the first route that
     matches, and a segment pattern would happily capture "new" as a client id. */
  { path: '/atelier/clients/new', area: 'backoffice', render: () => <ClientNewScreen /> },
  {
    path: '/atelier/clients/:clientId',
    area: 'backoffice',
    render: (params) => <ClientScreen clientId={params['clientId'] as string} />,
  },
  {
    path: '/atelier/clients/:clientId/records/new',
    area: 'backoffice',
    render: (params) => <MeasurementAuthoringScreen clientId={params['clientId'] as string} />,
  },
  {
    path: '/atelier/clients/:clientId/photographs/new',
    area: 'backoffice',
    render: (params) => <PhotographSetScreen clientId={params['clientId'] as string} />,
  },
];

function Router() {
  const { match, navigate } = useNavigation();
  const { phase, session } = useSession();

  if (phase === 'resolving') {
    // ST-SIGNIN-ALREADYIN: resolve BEFORE first paint. The door never flashes.
    return null;
  }

  if (match.path === '/' || match.path === '') {
    const landing = session === null ? '/signin' : session.landing;
    queueMicrotask(() => navigate(landing, { replace: true }));
    return null;
  }

  for (const route of ROUTES) {
    const params = matchPath(route.path, match.path);
    if (params === null) continue;

    if (route.area === 'public') {
      if (session !== null) {
        queueMicrotask(() => navigate(session.landing, { replace: true }));
        return null;
      }
      return route.render(params, match.query);
    }

    if (session === null) {
      queueMicrotask(() => navigate('/signin', { replace: true }));
      return null;
    }

    const staff = session.role !== 'client';
    const backOffice = session.role === 'master_tailor' || session.role === 'admin';
    const permitted =
      route.area === 'client'
        ? session.role === 'client'
        : route.area === 'staff'
          ? staff
          : backOffice;

    if (!permitted) return <RefusalScreen kind={403} />;
    return route.render(params, match.query);
  }

  return <RefusalScreen kind={404} />;
}

/** ST-403 and ST-404 — one component, one voice, identical status and copy. */
function RefusalScreen({ kind }: { kind: 403 | 404 }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const rooms =
    session?.role === 'client'
      ? [
          { href: '/wardrobe', label: t('global.nav.wardrobe') },
          { href: '/measurements', label: t('global.nav.measurements') },
          { href: '/book', label: t('global.nav.book') },
          { href: '/commission', label: t('global.nav.commission') },
        ]
      : [];
  return (
    <AppShell title={kind === 403 ? t('e403.title') : t('e404.title')} width="ledger">
      <Refused kind={kind} />
      {rooms.length === 0 ? null : (
        <section className="pt-section">
          <h2 className="pt-record__groupHeading">{t('global.rooms.heading')}</h2>
          <ul>
            {rooms.map((room) => (
              <li key={room.href} className="pt-row">
                <Link href={room.href} onNavigate={navigate}>
                  {room.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </AppShell>
  );
}

/**
 * An error boundary per state spec: a typeset statement in the house voice, a
 * route out, and a retry. Never a stack trace, never a machine code, never a
 * blank page.
 */
class ScreenBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    // Logged, never shown ([T-73]).
    console.error('[screen]', error);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <AppShell title={t('error.fetch.what')} width="ledger">
        <Statement>{t('error.fetch.means')}</Statement>
        <Statement>{t('error.fetch.do')}</Statement>
        <div className="pt-actions">
          <Button onClick={() => window.location.reload()}>{t('global.action.retry')}</Button>
        </div>
      </AppShell>
    );
  }
}

export function App() {
  return (
    <NavigationProvider>
      <SessionProvider>
        <AnnouncerProvider>
          <ScreenBoundary>
            <Router />
          </ScreenBoundary>
        </AnnouncerProvider>
      </SessionProvider>
    </NavigationProvider>
  );
}
